import React from 'react';
import styled from 'styled-components';
import { Grid, Divider } from '@mui/material';
import { type LanguageQualificationFormProps } from './types';
import { APPLICATION_TEXT } from '../../lang';
import { type RenderData } from '../../types';
import { getOptionsLabel } from '../../getOptionsLabel';
import { PAGE_TEXT } from '../../../../Application/components/ApplicationForm/lang';
import { LabelValue, DocumentBox } from '../../../../../components';
import { type LanguageQualificationResult } from '../../../../Application/graphql';
import {
  languageResultOrderMap,
  languageResultslabelMap,
  LANGUAGE_QUALIFICATION_TEST_TYPE,
  languageQualificationTestTypeOptions,
} from '../../../../Application/components';

const LANGUAGE_QUALIFICATION = PAGE_TEXT.QUALIFICATION.LANGUAGE_QUALIFICATION;

export const LanguageQualificationForm: React.FunctionComponent<
  LanguageQualificationFormProps
> = (props: LanguageQualificationFormProps) => {
  const { data } = props;
  const {
    needEnglishTest = true,
    testType,
    testDate,
    resultAnnouncedIndicator,
    expectedResultUpdateDate,
    result,
    referenceNo,
    supportingDocuments = [],
  } = data ?? {};
  const {
    resultReceiptDate,
    reading = '',
    listening = '',
    speaking = '',
    writing = '',
    overall = '',
  } = result ?? {};

  const languageResultMap: Partial<
    Record<keyof LanguageQualificationResult, string>
  > = {
    listening: listening.toString(),
    reading: reading.toString(),
    writing: writing.toString(),
    speaking: speaking.toString(),
    overall: overall.toString(),
  };

  // IELTS: listening/reading/writing/speaking/overall
  // TOEFL: reading/listening/speaking/writing/overall
  const resultsWithOrder =
    languageResultOrderMap[
      (testType as LANGUAGE_QUALIFICATION_TEST_TYPE) ??
        LANGUAGE_QUALIFICATION_TEST_TYPE.IELTS
    ];

  const resultFields: RenderData[] = resultsWithOrder?.map((field, index) => {
    const label = `${languageResultslabelMap?.[field]}`;
    return {
      label,
      value: languageResultMap?.[field],
      isFullWidth: index === 4, // for overall
    };
  });

  const renderData: RenderData[] = [
    {
      label: LANGUAGE_QUALIFICATION.TEST_TYPE,
      value: getOptionsLabel(
        languageQualificationTestTypeOptions,
        testType ?? '',
      ),
      isFullWidth: true,
    },
    ...(resultAnnouncedIndicator === true
      ? [
          {
            label: LANGUAGE_QUALIFICATION.TEST_DATE,
            value: testDate as React.ReactNode,
          },
          {
            label: LANGUAGE_QUALIFICATION.RESULT_RECEIPT_DATE,
            value: resultReceiptDate,
          },
          ...resultFields,
          {
            label:
              testType === LANGUAGE_QUALIFICATION_TEST_TYPE.IELTS
                ? LANGUAGE_QUALIFICATION.TEST_REPORT_FORM_NO
                : LANGUAGE_QUALIFICATION.APPOINTMENT_NO,
            value: referenceNo ?? APPLICATION_TEXT.NA,
            isFullWidth: true,
          },
        ]
      : [
          {
            label: LANGUAGE_QUALIFICATION.EXPECTED_UPDATE_DATE,
            value: expectedResultUpdateDate as React.ReactNode,
            isFullWidth: true,
          },
        ]),
  ];

  const hasFiles = supportingDocuments.length > 0;

  const supportingDocs = (
    <>
      <div className="p-[18px]">
        <h3 className="italic text-[#8c8c8c]">
          {APPLICATION_TEXT.SUPPORTING_DOCUMENT}
        </h3>
        <div className="p-2">
          {hasFiles ? (
            supportingDocuments.map((file, index) => {
              return <DocumentBox key={index} file={file} />;
            })
          ) : (
            <div>
              <p>{APPLICATION_TEXT.NA}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
  return data !== undefined ? (
    needEnglishTest ? (
      <Container>
        <Grid container spacing={2} padding={1}>
          {renderData.map((item, index) => {
            const isLastValue =
              index === renderData.length - 1 && renderData.length % 2 !== 0;
            return (
              <Grid key={index} size={isLastValue || item.isFullWidth ? 12 : 6}>
                <LabelValue label={item.label} value={item.value as string} />
                <Divider />
              </Grid>
            );
          })}
        </Grid>
        {supportingDocs}
      </Container>
    ) : (
      <>
        <div className="p-4">{'No English proficiency is required.'}</div>
        {supportingDocs}
      </>
    )
  ) : (
    <div>{APPLICATION_TEXT.NA}</div>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  .MuiBox-root {
    padding: 0 0 0.1rem 0.5rem;
  }

  .MuiGrid-item {
    padding-left: 0 !important;
  }

  .MuiGrid-container {
    padding: 0.5rem 1rem 0 2rem !important;
  }
`;
